/**
 * dashboard-v2 design tokens — the mockup design system
 * (docs/backend/shit-draw/dashboard-rework/mockups/assets/mockup.css) as
 * Tailwind class strings.
 *
 * House rules carried by these tokens:
 *  - Severity is words + a navy → slate → amber scale. NO red anywhere.
 *  - Confidence is HIGH / MEDIUM / LOW words, never a percentage or gauge.
 *
 * Class strings must stay LITERAL (Tailwind JIT scans source text for
 * arbitrary values) — never template a hex into a class at runtime.
 */

// Core palette (reference — the literal hexes below must match):
//   navy #000b57 · button #1d2fb5 · check #00139e · ink #001830
//   ink-soft #475068 · ink-muted #5d6584 · page #eef1f7 · line #d7deea
//   surface #e6ebf1 · sev-clear #6773a0 · sev-review #b45309
//   sev-review-bg #fdf3e4 · sev-review-border #e6a94e
// ink-muted deviates from the mockup's #707a93: that hex fails WCAG AA at
// the 12-13px sizes it is used at (4.04:1 on white); #5d6584 passes on all
// three page backgrounds (5.74:1 white, 4.79:1 surface, 5.07:1 page) and
// stays in the slate family.

export const PAGE_BG = 'bg-[#eef1f7]';

// --- Badges (mockup .badge-*) ------------------------------------------------

const BADGE_BASE =
  'inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11.5px] font-semibold leading-normal whitespace-nowrap border';

export const badgeOutline = `${BADGE_BASE} bg-white border-[#d7deea] text-[#475068]`;
export const badgeNavy = `${BADGE_BASE} bg-[#e6ebf1] border-[#c8d2e6] text-[#000b57]`;
export const badgeSlate = `${BADGE_BASE} bg-[#eef1f7] border-[#d3dae8] text-[#5d6584]`;
export const badgeAmber = `${BADGE_BASE} bg-[#fdf3e4] border-[#e6a94e] text-[#b45309]`;

/**
 * Modifier for a badge whose text is REPORT DATA rather than fixed UI copy —
 * a model-emitted theme, an interest, a list name. Compose it through `cn()`,
 * which drops the base `whitespace-nowrap` (#185).
 *
 * Why it matters beyond the chip: a 70-character interest in a nowrap chip is
 * ~445px wide, so at 412px the document grows wider than the viewport. Chrome
 * on mobile answers that by widening the LAYOUT viewport to fit the content
 * while the visual viewport stays at device width — and every `position: fixed`
 * element is laid out against the layout viewport, so the cookie banner went
 * with it, ~135px below the fold with its Accept button unclickable. One
 * unwrappable chip is enough to do that to the whole page.
 *
 * A status chip ("8 to review") keeps the nowrap default — it is short by
 * construction, and this modifier renders identically until text would
 * otherwise overflow.
 */
export const badgeFreeText = 'max-w-full whitespace-normal break-words';

// --- Confidence chips (mockup .conf-*) ---------------------------------------

const CONF_BASE =
  'inline-flex items-center gap-1 rounded px-[7px] py-0.5 text-[11px] font-bold tracking-[.02em]';

export const confHigh = `${CONF_BASE} bg-[#000b57] text-white`;
export const confMed = `${CONF_BASE} bg-[#6773a0] text-white`;
export const confLow = `${CONF_BASE} bg-white text-[#6773a0] border border-[#8d96b4]`;

// --- Provenance tags (mockup .prov-*) ----------------------------------------

const PROV_BASE =
  'inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11.5px] font-semibold border';

export const provYou = `${PROV_BASE} bg-[#e6f0ea] text-[#2f6b46] border-[#bcdcc8]`;
export const provInbound = `${PROV_BASE} bg-[#f1eef7] text-[#5b4b86] border-[#d6cdea]`;

// --- Posture pills (mockup .posture-*) ---------------------------------------

const POSTURE_BASE =
  'inline-flex items-center gap-1.5 rounded-full px-[11px] py-[3px] text-xs font-semibold';

export const postureReview = `${POSTURE_BASE} bg-[#e6ebf1] text-[#000b57]`;
export const postureContext = `${POSTURE_BASE} bg-[#fdf3e4] text-[#b45309] border border-[#e6a94e]`;
export const postureAttorney = `${POSTURE_BASE} bg-[#eef1f7] text-[#6773a0] border border-[#d3dae8]`;

// --- Finding-card severity rail (mockup .finding.{review,aware}) -------------
// Left rail colour carries the disposition; default (cleared/quiet) is slate.

export const findingRail: Record<'concern' | 'review' | 'cleared', string> = {
  concern: 'border-l-4 border-l-[#b45309]', // amber — worth a closer look
  review: 'border-l-4 border-l-[#000b57]', // navy — context to be aware of
  cleared: 'border-l-4 border-l-[#6773a0]', // slate — checked & cleared
};

// --- Finding-card head strip (FindingCard + GroupedFindingCard) ---------------
// Phone: a column — title, meta line, then the pill and provenance tag on one
// row. md+: a [meta | provenance | pill] grid — the title spans the first two
// columns, the tags row dissolves (md:contents) so the pill takes the right
// column and the provenance tag ends the meta line.

export const headStrip =
  'flex flex-col gap-1.5 border-b border-[#d7deea] bg-[#fcfdff] px-[15px] pb-[11px] pt-3 md:grid md:grid-cols-[auto_1fr_auto] md:items-center md:gap-x-2 md:gap-y-1';
export const headMeta =
  'flex flex-wrap items-center gap-2 text-[12.5px] leading-normal text-[#5d6584] md:col-start-1 md:row-start-2';
export const headTags = 'flex flex-wrap items-center gap-2 md:contents';
export const headPill = 'md:col-start-3 md:row-span-2 md:row-start-1 md:ml-2';
// nowrap: in a tight grid the meta line should wrap, not the tag's own words;
// self-start keeps the tag level with the meta's first line when it does.
export const headProvenance =
  'md:col-start-2 md:row-start-2 md:self-start md:justify-self-start md:whitespace-nowrap';

// --- Notes / callouts (mockup .note, .note.amber) -----------------------------

const NOTE_BASE =
  'flex items-start gap-2.5 rounded-lg px-3.5 py-3 text-[13px] leading-normal border';

export const noteNavy = `${NOTE_BASE} bg-[#e6ebf1] border-[#c8d2e6] text-[#001830]`;
export const noteAmber = `${NOTE_BASE} bg-[#fdf3e4] border-[#e6a94e] text-[#001830]`;

// --- Status dots (mockup .dot-*) ----------------------------------------------

export const dotClear = 'inline-block h-[9px] w-[9px] rounded-full bg-[#6773a0]';
export const dotAware = 'inline-block h-[9px] w-[9px] rounded-full bg-[#000b57]';
export const dotReview = 'inline-block h-[9px] w-[9px] rounded-full bg-[#b45309]';

// --- Text ----------------------------------------------------------------------

export const inkText = 'text-[#001830]';
export const inkSoftText = 'text-[#475068]';
export const inkMutedText = 'text-[#5d6584]';

// --- Cards ----------------------------------------------------------------------

export const cardShell =
  'rounded-lg border border-[#d7deea] bg-white shadow-[0_1px_2px_rgba(0,11,87,.06),0_1px_3px_rgba(0,11,87,.05)]';
